import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  ButtonComponent,
  ButtonColour,
  ButtonSize,
  ButtonType,
} from '@vaad1st/component.atom.button';
import {
  HeroBannerComponent,
  HeroBannerProps,
  TitleSize,
} from '@vaad1st/component.molecule.hero-banner';
import styles from './slide-show.component.module.less';
import classNames from 'classnames';
import useMediaQuery, { BreakPointToken } from 'src/hooks/use-media-query';
import { preprocessHTMLWithLinks } from 'src/utils/component-utils/utils';
import { HeadingElement } from 'src/utils/shared-types';
import { ContainerComponent } from '@vaad1st/component.layout.container';

interface SlideShowComponentProps extends HeroBannerProps {
  titleElement?: HeadingElement;
}

interface SlideShowProps {
  slides: SlideShowComponentProps[];
  interval?: number;
}

const PauseIcon = () => (
  <svg
    width="8"
    height="8"
    viewBox="0 0 8 8"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M1.5 1L1.5 7"
      stroke="#030C16"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M6.5 1L6.5 7"
      stroke="#030C16"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const PlayIcon = () => (
  <svg
    width="8"
    height="8"
    viewBox="0 0 8 8"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      id="Vector"
      fillRule="evenodd"
      clipRule="evenodd"
      d="M2.15598 1.10404L5.97788 3.36449C6.46127 3.65 6.46127 4.35 5.97788 4.63551L2.15598 6.89596C1.66372 7.18738 1.0415 6.83196 1.0415 6.25996V1.74004C1.0415 1.16803 1.66372 0.812624 2.15598 1.10404Z"
      stroke="#030C16"
      strokeWidth="1.47678"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const NextIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6.66675 10.6666L9.33341 7.99998L6.66675 5.33331"
      stroke="#030C16"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SlideShow: React.FC<SlideShowProps> = ({ slides, interval = 5000 }) => {
  const [isPlaying, setIsPlaying] = useState(true);
  const [count, setCount] = useState<number>(0);
  const [autoRotate, setAutoRotate] = useState(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [key, setKey] = useState<number>(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [items] = useState<HeroBannerProps[]>(
    slides.map((item) => ({
      ...item,
      text: item.text ? preprocessHTMLWithLinks(item.text) : '',
      titleElement: item.titleElement || HeadingElement.H2,
      titleSize: item.titleSize || TitleSize.Mdm,
    }))
  );
  const [controlsYPosition, setControlsYPosition] = useState<number>(200);
  const isMobile = useMediaQuery(`(max-width: ${BreakPointToken.SmMax}px)`);
  const isTablet = useMediaQuery(`(max-width: ${BreakPointToken.MdMax}px)`);

  const INTERVAL_FREQUENCY = 1000;

  // Function to restart the animation by changing the key
  const restartAnimation = () => {
    setKey((prev) => prev + 1); // Change key to force re-render
  };

  const scrollToIndex = (index: number) => {
    const container = containerRef.current;
    if (!container) return;

    const slideWidth = container.offsetWidth;
    container.scrollTo({
      left: index * slideWidth,
      behavior: 'smooth',
    });
  };

  // Go to next slide
  const nextSlide = () => {
    if (!containerRef.current || itemRefs.current.length === 0) return;

    setCurrentIndex((prevIndex) => {
      const next = prevIndex + 1;
      return next >= items.length ? 0 : next;
    });
  };

  // // Go to previous slide
  const prevSlide = () => {
    if (!containerRef.current || itemRefs.current.length === 0) return;

    setCurrentIndex((prevIndex) => {
      const prev = prevIndex - 1;
      return prev < 0 ? items.length - 1 : prev;
    });
  };

  // // Toggle play/pause
  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  const positionControls = useCallback(() => {
    if (!containerRef.current || itemRefs.current.length === 0) return;
    const currentItem = itemRefs.current[currentIndex];
    const desktopImageContainer =
      currentItem && currentItem.querySelector('div[class*="desktopImage"]');
    const mobileImageContainer =
      currentItem && currentItem.querySelector('div[class*="mobileImage"]');
    const height = isMobile
      ? mobileImageContainer?.getBoundingClientRect().height
      : desktopImageContainer?.getBoundingClientRect().height;
    const OVERLAP = isTablet ? 70 : 50;

    if (height) {
      setControlsYPosition(height - OVERLAP);
    }
  }, [
    currentIndex,
    itemRefs,
    containerRef,
    isMobile,
    isTablet,
  ]);

  const reset = useCallback(() => {
    setCount(0);
    restartAnimation();
  }, []);

  // Every time the slide index changes, scroll to the new index
  useEffect(() => {
    if (itemRefs.current.length > 0) {
      scrollToIndex(currentIndex);
    }
    positionControls();
    reset();
  }, [currentIndex, itemRefs, positionControls, reset]);

  // Every time the interval changes exceeds the interval, go to the next slide
  useEffect(() => {
    const intervalInSec = interval / INTERVAL_FREQUENCY;
    if (count >= intervalInSec) {
      setCurrentIndex((prevIndex) => {
        const next = prevIndex + 1;
        const nextIndex = next >= items.length ? 0 : next;
        return nextIndex;
      });
    }
  }, [count, interval, items.length]);

  // Function to start the interval
  const startInterval = () => {
    if (!intervalRef.current) {
      intervalRef.current = setInterval(() => {
        setCount((prev) => prev + 1);
      }, INTERVAL_FREQUENCY);
    }
  };

  // Function to stop the interval
  const stopInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const pauseSlideShow = useCallback(() => {
    if (isPlaying && autoRotate) {
      setIsPlaying(false);
    }
  }, [isPlaying, autoRotate]);

  useEffect(() => {
    if (isPlaying) {
      startInterval();
    } else {
      stopInterval();
    }
    return () => stopInterval();
  }, [isPlaying]);

  useEffect(() => {
    if (!autoRotate) {
      pauseSlideShow();
    }
  }, [autoRotate, pauseSlideShow]);

  const startSlideShow = () => {
    if (!isPlaying && autoRotate) {
      setIsPlaying(true);
    }
  };

  const handlePausePlay = () => {
    setAutoRotate(true);
    togglePlay();
  };

  const handlePrevSlide = () => {
    prevSlide();
    setAutoRotate(false);
    setIsPlaying(false);
  };

  const handleNextSlide = () => {
    nextSlide();
    setAutoRotate(false);
    setIsPlaying(false);
  };

  return (
    <div
      className={classNames(styles['slide-show'])}
      data-testid="slide-show"
      onMouseOver={pauseSlideShow}
      onMouseLeave={startSlideShow}
    >
      <div className={styles['slide-show__container']}>
        <div
          ref={containerRef}
          className={classNames(styles['slide-show__wrapper'])}
          data-testid="slide-show__wrapper"
        >
          {items.map((item, index) => {
            return (
              <div
                ref={(el) => {
                  itemRefs.current[index] = el;
                }}
                className={styles['slide-show__slide']}
                key={index}
              >
                <HeroBannerComponent {...item} />
              </div>
            );
          })}
        </div>
      </div>
      {items.length > 1 && (
        <div
          className={styles['slide-show__controls-wrapper']}
          {...(isMobile || isTablet
            ? { style: { top: `${controlsYPosition}px` } }
            : {})}
        >
          <ContainerComponent
            as="div"
            className={styles['slide-show__controls-wrapper__inner']}
          >
            <div
              data-testid="slide-show__controls"
              className={styles['slide-show__controls']}
            >
              <div
                key={key}
                className={classNames(
                  styles['round-button'],
                  styles['round-button--play'],
                  !isPlaying && styles['round-button--paused']
                )}
              >
                <ButtonComponent
                  iconChild={isPlaying ? <PauseIcon /> : <PlayIcon />}
                  colour={ButtonColour.Inverse}
                  size={ButtonSize.XSmall}
                  type={ButtonType.Button}
                  onClick={handlePausePlay}
                  className={
                    isPlaying ? styles['play-button'] : styles['pause-button']
                  }
                />
              </div>
              <div
                className={classNames(
                  styles['round-button'],
                  styles['round-button--prev']
                )}
              >
                <ButtonComponent
                  iconChild={<NextIcon />}
                  colour={ButtonColour.Inverse}
                  size={ButtonSize.XSmall}
                  type={ButtonType.Button}
                  onClick={handlePrevSlide}
                />
              </div>
              <div
                className={classNames(
                  styles['round-button'],
                  styles['round-button--next']
                )}
              >
                <ButtonComponent
                  iconChild={<NextIcon />}
                  colour={ButtonColour.Inverse}
                  size={ButtonSize.XSmall}
                  type={ButtonType.Button}
                  onClick={handleNextSlide}
                />
              </div>
            </div>
          </ContainerComponent>
        </div>
      )}
    </div>
  );
};

export default SlideShow;
